import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { money } from "@/lib/format";
import PbxExtensions from "@/pages/admin/PbxExtensions";
import {
  Buildings, Crown, ShieldCheck, Users, Phone, Sparkle, Check, ArrowRight,
  Plus, Trash, X, Copy, PhoneCall, Clock, Play, UsersThree,
} from "@phosphor-icons/react";

const CYCLES = [
  ["annual", "Annual", "annual"],
  ["semi", "6 months", "semi"],
  ["quarterly", "Quarterly", "quarterly"],
  ["monthly", "Monthly", "monthly"],
];

export default function Business() {
  const { refresh, isStaff } = useAuth();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ["account-status"],
    queryFn: async () => (await api.get("/account/status")).data,
  });

  const reloadAll = async () => { await refetch(); await refresh(); qc.invalidateQueries(); };

  if (isLoading || !status) {
    return <div className="flex min-h-[50vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  // Has an organization → full Org-Admin Console.
  if (status.has_org) return <Console status={status} onChange={reloadAll} />;

  // Entitled (active Business/Enterprise package) OR platform staff → straight to
  // the console/onboarding, never the paywall. Staff get immediate business access.
  if (status.business_entitled || isStaff) return <Wizard status={status} onDone={reloadAll} />;

  // Otherwise → Upgrade / package selection.
  return <Upgrade status={status} onChange={reloadAll} intentEnterprise={params.get("intent") === "enterprise"} />;
}

/* ----------------------------- Upgrade / packages ----------------------------- */
function Upgrade({ status, onChange, intentEnterprise }) {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("annual");
  const prices = status.pbx_prices || {};
  const subscribe = useMutation({
    mutationFn: async () => (await api.post(`/pbx/business/subscribe?cycle=${cycle}`)).data,
    onSuccess: () => { toast.success("Business package activated!"); onChange(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const cardCheckout = useMutation({
    mutationFn: async () => (await api.post("/ai-sales/pbx-checkout", { cycle, origin_url: window.location.origin })).data,
    onSuccess: (d) => { if (d.checkout_url) window.location.href = d.checkout_url; },
    onError: (e) => toast.error(apiError(e)),
  });
  const reqEnterprise = useMutation({
    mutationFn: async () => (await api.post("/enterprise/request")).data,
    onSuccess: () => { toast.success("Enterprise request sent — our team will reach out."); onChange(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const priceFor = (c) => ({ monthly: prices.monthly, quarterly: prices.quarterly, semi: prices.semi, annual: prices.annual }[c]);

  return (
    <div className="space-y-8" data-testid="business-upgrade">
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary"><Buildings size={14} weight="fill" /> Upgrade your account</span>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Bring your business to iYeneh</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">A Business package unlocks your own Organization, a 5-digit extension phone system, an SI Receptionist and the Org-Admin Console — pay instantly by card or from your wallet.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Business */}
        <div className="rounded-3xl border border-primary/40 bg-card p-6 shadow-xl" data-testid="plan-business">
          <div className="flex items-center gap-2"><ShieldCheck size={22} weight="fill" className="text-primary" /><h2 className="font-display text-xl font-semibold">Business</h2></div>
          <p className="mt-1 text-sm text-muted-foreground">Everything you need to run a cloud PBX for your team.</p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="cycle-select">
            {CYCLES.map(([k, label]) => (
              <button key={k} data-testid={`cycle-${k}`} onClick={() => setCycle(k)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${cycle === k ? "border-primary bg-primary/10 text-primary" : "border-white/10 text-muted-foreground hover:border-primary/40"}`}>
                {label}{priceFor(k) != null ? ` · ${money(priceFor(k))}` : ""}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs font-semibold text-emerald-500" data-testid="annual-savings-note">💡 Annual = 2 months free — best value, covered all year.</p>
          <ul className="mt-5 space-y-2 text-sm">
            {["Your own Organization / Tenant", "00000–99999 five-digit extensions", "SI Receptionist + Intent Phrasebook", "Org-Admin Console & members", "Business hours & after-hours routing"].map((f) => (
              <li key={f} className="flex items-center gap-2"><Check size={16} weight="bold" className="text-emerald-500" /> {f}</li>
            ))}
          </ul>
          <button data-testid="business-pay-card" onClick={() => cardCheckout.mutate()} disabled={cardCheckout.isPending || !priceFor(cycle)}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.01] disabled:opacity-60">
            {cardCheckout.isPending ? "Redirecting to secure checkout…" : priceFor(cycle) ? <>Pay by card — {money(priceFor(cycle))} <ArrowRight size={18} weight="bold" /></> : "Loading price…"}
          </button>
          <button data-testid="business-subscribe" onClick={() => subscribe.mutate()} disabled={subscribe.isPending}
            className="mt-2 w-full rounded-full border border-primary/40 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-60">
            {subscribe.isPending ? "Activating…" : "Or pay from wallet balance"}
          </button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Instant activation. Cancel anytime — your data is preserved.</p>
        </div>

        {/* Enterprise */}
        <div className={`rounded-3xl border bg-card p-6 ${intentEnterprise ? "border-amber-500/50" : "border-white/10"}`} data-testid="plan-enterprise">
          <div className="flex items-center gap-2"><Crown size={22} weight="fill" className="text-amber-500" /><h2 className="font-display text-xl font-semibold">Enterprise</h2></div>
          <p className="mt-1 text-sm text-muted-foreground">{status.enterprise_contact_note || "Higher limits, advanced admin controls and a custom package for larger teams."}</p>
          {(status.enterprise_plans || []).length > 0 && (
            <ul className="mt-4 space-y-2 text-sm">
              {status.enterprise_plans.map((p, i) => (
                <li key={i} className="flex items-center justify-between rounded-xl border border-white/10 px-3 py-2">
                  <span className="font-medium">{p.name}</span>
                  {p.price_cents != null && <span className="font-mono-metric text-sm text-muted-foreground">{money(p.price_cents)}</span>}
                </li>
              ))}
            </ul>
          )}
          <ul className="mt-5 space-y-2 text-sm">
            {["Everything in Business", "Higher extension & member limits", "Advanced admin controls", "Priority support"].map((f) => (
              <li key={f} className="flex items-center gap-2"><Check size={16} weight="bold" className="text-amber-500" /> {f}</li>
            ))}
          </ul>
          {status.enterprise_status === "requested" ? (
            <div className="mt-6 rounded-full bg-amber-500/10 py-3.5 text-center text-sm font-semibold text-amber-500" data-testid="enterprise-pending">Request received — we'll be in touch</div>
          ) : (
            <button data-testid="enterprise-contact" onClick={() => reqEnterprise.mutate()} disabled={reqEnterprise.isPending}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border-2 border-amber-500/50 py-3.5 font-semibold text-amber-500 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
              {reqEnterprise.isPending ? "Sending…" : "Contact Sales"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Onboarding wizard ----------------------------- */
const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const TZ = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Africa/Douala", "Africa/Lagos", "UTC"];

function Wizard({ status, onDone }) {
  const [f, setF] = useState({
    org_name: status.pending_org_name || "", timezone: "America/New_York",
    bh_open: "08:00", bh_close: "20:00", bh_days: [0, 1, 2, 3, 4],
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleDay = (d) => setF((s) => ({ ...s, bh_days: s.bh_days.includes(d) ? s.bh_days.filter((x) => x !== d) : [...s.bh_days, d].sort() }));

  const onboard = useMutation({
    mutationFn: async () => (await api.post("/business/onboard", f)).data,
    onSuccess: () => { toast.success("Your business PBX is ready 🎉"); onDone(); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="business-wizard">
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15"><Sparkle size={28} weight="fill" className="text-primary" /></div>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight">Set up your business in minutes</h1>
        <p className="mt-2 text-muted-foreground">We'll create your organization, system extensions, an SI Receptionist and a starter phrasebook automatically.</p>
      </div>

      <div className="space-y-5 rounded-3xl border border-white/10 bg-card p-6">
        <label className="block text-sm font-semibold">Business name
          <input data-testid="wizard-org-name" value={f.org_name} onChange={(e) => set("org_name", e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm outline-none focus:border-primary" placeholder="Acme Insurance" />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block text-sm font-semibold">Time zone
            <select data-testid="wizard-tz" value={f.timezone} onChange={(e) => set("timezone", e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm">
              {TZ.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm font-semibold">Opens
              <input data-testid="wizard-open" type="time" value={f.bh_open} onChange={(e) => set("bh_open", e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-3 py-3 text-sm" />
            </label>
            <label className="block text-sm font-semibold">Closes
              <input data-testid="wizard-close" type="time" value={f.bh_close} onChange={(e) => set("bh_close", e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-3 py-3 text-sm" />
            </label>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold">Open days</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="wizard-days">
            {DAYS.map(([label, d]) => (
              <button key={d} type="button" onClick={() => toggleDay(d)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${f.bh_days.includes(d) ? "border-primary bg-primary/10 text-primary" : "border-white/10 text-muted-foreground"}`}>{label}</button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-white/15 p-4 text-sm text-muted-foreground">
          <p className="font-semibold text-foreground">We'll create automatically:</p>
          <ul className="mt-2 space-y-1">
            {["Reception (00000), SI Receptionist (00001), Operator (00002), Voicemail (00003), After-Hours (00004)", "A starter SI phrasebook", "Standard SI confidence", "Business hours & after-hours routing"].map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={15} weight="bold" className="mt-0.5 text-emerald-500" /> {x}</li>
            ))}
          </ul>
        </div>

        <button data-testid="wizard-submit" onClick={() => onboard.mutate()} disabled={onboard.isPending || !f.org_name.trim() || f.bh_days.length === 0}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.01] disabled:opacity-50">
          {onboard.isPending ? "Setting up…" : <>Create my business PBX <ArrowRight size={18} weight="bold" /></>}
        </button>
      </div>
    </div>
  );
}

/* ----------------------------- Org-Admin Console ----------------------------- */
function Console({ status, onChange }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const levelBadge = status.account_level === "enterprise"
    ? { label: "Enterprise", cls: "bg-amber-500/15 text-amber-500", icon: Crown }
    : { label: "Business", cls: "bg-primary/15 text-primary", icon: ShieldCheck };
  const Badge = levelBadge.icon;

  return (
    <div className="space-y-6" data-testid="business-console">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${levelBadge.cls}`}><Badge size={14} weight="fill" /> {levelBadge.label}</span>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{status.org_name || "My Business"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Org-Admin Console — manage your own organization.</p>
        </div>
      </div>

      <PresenceStrip />

      <div className="flex flex-wrap gap-1 border-b border-white/10" data-testid="business-tabs">
        {[["overview", "Overview"], ["members", "Members"], ["pbx", "Cloud PBX"], ["ringgroups", "Ring Groups"], ["settings", "Call Settings"]].map(([k, label]) => (
          <button key={k} data-testid={`biz-tab-${k}`} onClick={() => (k === "settings" ? navigate("/app/telephony") : setTab(k))}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview status={status} onChange={onChange} go={setTab} />}
      {tab === "members" && <Members />}
      {tab === "pbx" && <PbxExtensions orgAdmin />}
      {tab === "ringgroups" && <RingGroups />}
    </div>
  );
}

function Overview({ status, onChange, go }) {
  const { data: members } = useQuery({ queryKey: ["pbx-members"], queryFn: async () => (await api.get("/pbx/members")).data });
  const { data: exts } = useQuery({ queryKey: ["pbx-extensions", ""], queryFn: async () => (await api.get("/pbx/extensions")).data });
  const sub = status.pbx_sub || {};
  const cards = [
    { label: "Members", value: members?.members?.length ?? 0, icon: Users, tab: "members" },
    { label: "Extensions", value: exts?.extensions?.length ?? 0, icon: Phone, tab: "pbx" },
  ];
  return (
    <div className="space-y-6" data-testid="business-overview">
      <GettingStarted />
      <div className="grid gap-3 sm:grid-cols-3">
        {cards.map((c) => (
          <button key={c.label} onClick={() => go(c.tab)} data-testid={`ov-${c.label.toLowerCase()}`}
            className="flex items-center gap-4 rounded-2xl border border-white/10 bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><c.icon size={22} weight="duotone" /></div>
            <div><p className="font-mono-metric text-2xl font-semibold">{c.value}</p><p className="text-xs text-muted-foreground">{c.label}</p></div>
          </button>
        ))}
        <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-card p-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500"><Clock size={22} weight="duotone" /></div>
          <div>
            <p className="text-sm font-semibold">{sub.active ? "Active" : status.account_level === "enterprise" ? "Enterprise" : "Restricted"}</p>
            <p className="text-xs text-muted-foreground">{sub.renews_at ? `Renews ${String(sub.renews_at).slice(0, 10)}` : "Subscription"}</p>
          </div>
        </div>
      </div>
      {!status.business_entitled && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm" data-testid="sub-lapsed">
          <p className="font-semibold text-amber-500">Your Business package is inactive</p>
          <p className="mt-1 text-muted-foreground">Your organization and data are preserved, but paid Business features are restricted. Reactivate to resume.</p>
          <button onClick={onChange} className="mt-3 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Refresh status</button>
        </div>
      )}
    </div>
  );
}

function Members() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["pbx-members"], queryFn: async () => (await api.get("/pbx/members")).data });
  const members = data?.members || [];
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/pbx/members/${id}`)).data,
    onSuccess: () => { toast.success("Member removed"); qc.invalidateQueries({ queryKey: ["pbx-members"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-4" data-testid="business-members">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">People in your organization. Assign them a 5-digit extension from the Cloud PBX tab.</p>
        <button data-testid="member-add" onClick={() => setShow(true)}
          className="flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105"><Plus size={16} weight="bold" /> Add member</button>
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : members.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-card p-10 text-center">
          <Users size={26} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No members yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Add your first team member to start assigning extensions.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-card">
          {members.map((m) => (
            <div key={m.id} data-testid={`member-row-${m.id}`} className="grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-3 text-sm last:border-0">
              <div className="col-span-5 truncate">
                <p className="font-medium">{m.name || m.email}</p>
                <p className="truncate text-xs text-muted-foreground">{m.email}{m.pending ? " · invite pending" : ""}</p>
              </div>
              <div className="col-span-3 text-xs text-muted-foreground">{m.role === "org_admin" ? "Admin" : "Member"}</div>
              <div className="col-span-2 font-mono-metric text-sm">{m.ext || <span className="text-xs italic text-muted-foreground">no ext</span>}</div>
              <div className="col-span-2 flex justify-end">
                {m.role !== "org_admin" && (
                  <button data-testid={`member-del-${m.id}`} onClick={() => { if (window.confirm(`Remove ${m.email} from your organization?`)) remove.mutate(m.id); }}
                    className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {show && <AddMember onClose={() => setShow(false)} onSaved={() => { setShow(false); qc.invalidateQueries({ queryKey: ["pbx-members"] }); }} />}
    </div>
  );
}

function AddMember({ onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const add = useMutation({
    mutationFn: async () => (await api.post("/pbx/members", { email, name })).data,
    onSuccess: (d) => {
      if (d.created && d.temp_password) { setCreated(d); toast.success("Member created"); }
      else { toast.success("Member added to your organization"); onSaved(); }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="member-form-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-card p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Add member</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        {created ? (
          <div className="space-y-3" data-testid="member-created">
            <p className="text-sm text-muted-foreground">Account created for <strong>{created.member.email}</strong>. Share this temporary password — they'll be asked to change it at first sign-in.</p>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-background px-3 py-2.5">
              <code className="font-mono-metric text-sm">{created.temp_password}</code>
              <button onClick={() => { navigator.clipboard?.writeText(created.temp_password); toast("Copied"); }} className="text-muted-foreground hover:text-primary"><Copy size={16} /></button>
            </div>
            <button onClick={onSaved} className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground">Done</button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-muted-foreground">Email
              <input data-testid="member-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm" placeholder="teammate@company.com" />
            </label>
            <label className="block text-xs font-semibold text-muted-foreground">Name (optional)
              <input data-testid="member-name" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm" placeholder="Jordan Lee" />
            </label>
            <p className="text-xs text-muted-foreground">If they already have an iYeneh account, we'll add them to your organization. Otherwise we'll create one with a temporary password.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">Cancel</button>
              <button data-testid="member-save" onClick={() => add.mutate()} disabled={add.isPending || !email.includes("@")}
                className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">{add.isPending ? "Adding…" : "Add member"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------- Getting started (first extension + test + SI preview) --------------------- */
function GettingStarted() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [testResult, setTestResult] = useState(null);
  const { data: exts } = useQuery({ queryKey: ["pbx-extensions", ""], queryFn: async () => (await api.get("/pbx/extensions")).data });
  const mine = (exts?.extensions || []).find((e) => e.user_id === user?.id && !e.system);

  const suggest = useQuery({ queryKey: ["pbx-next"], queryFn: async () => (await api.get("/pbx/extensions/next")).data, enabled: !mine });

  const assign = useMutation({
    mutationFn: async () => (await api.post("/pbx/extensions", {
      ext: suggest.data?.ext, name: user?.name || "My extension", user_id: user?.id,
      department: "management", permission: "domestic",
    })).data,
    onSuccess: () => { toast.success("Your extension is ready 🎉"); qc.invalidateQueries({ queryKey: ["pbx-extensions"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const testExt = useMutation({
    mutationFn: async () => (await api.post("/pbx/dial-resolve", { input: mine?.ext })).data,
    onSuccess: (d) => { setTestResult(d); if (d.kind === "internal") toast.success(`Extension ${mine.ext} rings ${d.member?.online ? "your app (online)" : "your app, then fallback"}`); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-4" data-testid="getting-started">
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <div className="flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-primary" /><h3 className="font-display text-lg font-semibold">Get your business calling</h3></div>
        <div className="mt-4 space-y-3">
          {/* Step 1: assign first extension */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-card p-4">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${mine ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary"}`}>{mine ? <Check size={16} weight="bold" /> : "1"}</span>
              <div>
                <p className="text-sm font-semibold">Assign your first extension to yourself</p>
                <p className="text-xs text-muted-foreground">{mine ? `You're on extension ${mine.ext}` : `Suggested: ${suggest.data?.ext || "…"}`}</p>
              </div>
            </div>
            {!mine && (
              <button data-testid="gs-assign-self" onClick={() => assign.mutate()} disabled={assign.isPending || !suggest.data?.ext}
                className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">{assign.isPending ? "Assigning…" : `Assign ${suggest.data?.ext || ""} to me`}</button>
            )}
          </div>
          {/* Step 2: test my extension */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-card p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">2</span>
              <div>
                <p className="text-sm font-semibold">Test my extension</p>
                <p className="text-xs text-muted-foreground">{testResult ? (testResult.kind === "internal" ? `Resolves to you · ${testResult.member?.online ? "online" : "offline (will ring app then fallback)"}` : (testResult.reason || "Not reachable")) : "Check your extension routes correctly."}</p>
              </div>
            </div>
            <button data-testid="gs-test-ext" onClick={() => testExt.mutate()} disabled={!mine || testExt.isPending}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold disabled:opacity-40"><PhoneCall size={15} weight="fill" /> {testExt.isPending ? "Testing…" : "Test my extension"}</button>
          </div>
          {/* Step 3: live SI onboarding preview */}
          <AiPreview />
        </div>
      </div>
    </div>
  );
}

function AiPreview() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: async () => (await api.post("/pbx/intents/test", { input })).data,
    onSuccess: (d) => setResult(d),
    onError: (e) => toast.error(apiError(e)),
  });
  const color = result?.decision === "route" ? "text-emerald-500" : result?.decision === "confirm" ? "text-amber-500" : "text-muted-foreground";
  return (
    <div className="rounded-xl border border-white/10 bg-card p-4" data-testid="gs-ai-preview">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">3</span>
        <div><p className="text-sm font-semibold">Live SI Receptionist preview</p><p className="text-xs text-muted-foreground">Type what a caller might say and see how your SI would route it.</p></div>
      </div>
      <div className="mt-3 flex gap-2">
        <input data-testid="gs-ai-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder='e.g. "I need to speak to someone about my bill"'
          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <button data-testid="gs-ai-run" onClick={() => run.mutate()} disabled={!input.trim() || run.isPending}
          className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40"><Play size={15} weight="fill" /> {run.isPending ? "Thinking…" : "Preview"}</button>
      </div>
      {result && (
        <div className="mt-3 rounded-lg bg-secondary p-3 text-sm" data-testid="gs-ai-result">
          <p className={`font-semibold uppercase ${color}`}>{result.decision}</p>
          {result.ext ? <p className="mt-0.5">→ {result.destination_name || result.ext} <span className="font-mono-metric">({result.ext})</span></p> : <p className="mt-0.5 text-muted-foreground">Would ask a clarifying question.</p>}
          {result.say && <p className="mt-1 text-xs italic text-muted-foreground">SI would say: "{result.say}"</p>}
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Ring Groups --------------------------------- */
function RingGroups() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["ring-groups"], queryFn: async () => (await api.get("/pbx/ring-groups")).data });
  const { data: exts } = useQuery({ queryKey: ["pbx-extensions", ""], queryFn: async () => (await api.get("/pbx/extensions")).data });
  const { data: nextExt } = useQuery({ queryKey: ["pbx-next-rg"], queryFn: async () => (await api.get("/pbx/extensions/next")).data });
  const { data: tel } = useQuery({ queryKey: ["telephony"], queryFn: async () => (await api.get("/telephony/settings")).data });
  const groups = data?.ring_groups || [];
  const assignable = (exts?.extensions || []).filter((e) => !e.system);
  const inboundRg = tel?.settings?.inbound_ring_group || "";

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/pbx/ring-groups/${id}`)).data,
    onSuccess: () => { toast.success("Ring group deleted"); qc.invalidateQueries({ queryKey: ["ring-groups"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const setInbound = useMutation({
    mutationFn: async (ext) => (await api.put("/telephony/settings", { inbound_ring_group: ext })).data,
    onSuccess: () => { toast.success("Inbound routing updated"); qc.invalidateQueries({ queryKey: ["telephony"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-4" data-testid="business-ringgroups">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Ring several extensions at once. Dial the group's number and everyone rings together (or in turn).</p>
        <button data-testid="rg-add" onClick={() => { setEdit(null); setShow(true); }}
          className="flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105"><Plus size={16} weight="bold" /> New ring group</button>
      </div>
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-card p-4" data-testid="inbound-rg-control">
          <PhoneCall size={18} weight="duotone" className="text-primary" />
          <span className="text-sm font-medium">Route incoming business calls to:</span>
          <select data-testid="inbound-rg-select" value={inboundRg} onChange={(e) => setInbound.mutate(e.target.value)}
            className="rounded-lg border border-white/10 bg-background px-3 py-1.5 text-sm">
            <option value="">Default (softphone / voicemail)</option>
            {groups.map((g) => <option key={g.id} value={g.ext}>{g.name} · {g.ext}</option>)}
          </select>
          <span className="text-xs text-muted-foreground">Applies business hours &amp; voicemail fallback.</span>
        </div>
      )}
      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-card p-10 text-center">
          <UsersThree size={26} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No ring groups yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Create one, e.g. "Sales Team" on 00200 ringing all your sales extensions.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {groups.map((g) => (
            <div key={g.id} data-testid={`rg-${g.id}`} className="rounded-2xl border border-white/10 bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{g.name} <span className="font-mono-metric text-sm text-muted-foreground">· {g.ext}</span></p>
                  <p className="text-xs text-muted-foreground">{g.strategy.replace("_", " ")} · {g.members.length} member{g.members.length === 1 ? "" : "s"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`rg-edit-${g.id}`} onClick={() => { setEdit(g); setShow(true); }} className="text-xs text-primary hover:underline">Edit</button>
                  <button data-testid={`rg-del-${g.id}`} onClick={() => { if (window.confirm(`Delete ring group "${g.name}"?`)) del.mutate(g.id); }} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {g.members.map((m) => (
                  <span key={m.ext} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                    <span className={`h-1.5 w-1.5 rounded-full ${m.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />{m.ext} {m.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {show && <RingGroupForm group={edit} assignable={assignable} nextExt={nextExt?.ext}
        onClose={() => setShow(false)} onSaved={() => { setShow(false); qc.invalidateQueries({ queryKey: ["ring-groups"] }); }} />}
    </div>
  );
}

function RingGroupForm({ group, assignable, nextExt, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [ext, setExt] = useState(group?.ext || nextExt || "");
  const [strategy, setStrategy] = useState(group?.strategy || "ring_all");
  const [members, setMembers] = useState(group?.member_exts || []);
  const toggle = (mx) => setMembers((s) => (s.includes(mx) ? s.filter((x) => x !== mx) : [...s, mx]));
  const save = useMutation({
    mutationFn: async () => {
      const body = { name, strategy, member_exts: members };
      if (group) return (await api.put(`/pbx/ring-groups/${group.id}`, body)).data;
      return (await api.post("/pbx/ring-groups", { ...body, ext })).data;
    },
    onSuccess: () => { toast.success(group ? "Ring group updated" : "Ring group created"); onSaved(); },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="rg-form-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-card p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">{group ? "Edit ring group" : "New ring group"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-semibold text-muted-foreground">Group name
            <input data-testid="rg-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm" placeholder="Sales Team" />
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Group extension
            <input data-testid="rg-ext" value={ext} disabled={!!group} onChange={(e) => setExt(e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 font-mono-metric text-sm disabled:opacity-60" placeholder="00200" />
          </label>
        </div>
        <label className="mt-3 block text-xs font-semibold text-muted-foreground">Ring strategy
          <select data-testid="rg-strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm">
            <option value="ring_all">Ring all at once</option>
            <option value="round_robin">Round-robin (rotate)</option>
            <option value="longest_idle">Longest idle first</option>
          </select>
        </label>
        <div className="mt-3">
          <p className="text-xs font-semibold text-muted-foreground">Members</p>
          <div className="mt-1.5 max-h-48 space-y-1 overflow-auto rounded-lg border border-white/10 bg-background p-2">
            {assignable.length === 0 ? <p className="p-2 text-xs text-muted-foreground">Create some extensions first (Cloud PBX tab).</p> :
              assignable.map((e) => (
                <label key={e.ext} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary">
                  <input type="checkbox" data-testid={`rg-member-${e.ext}`} checked={members.includes(e.ext)} onChange={() => toggle(e.ext)} className="h-4 w-4" />
                  <span className="font-mono-metric">{e.ext}</span> {e.name}
                </label>
              ))}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">Cancel</button>
          <button data-testid="rg-save" onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || ext.length !== 5 || members.length === 0}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">{save.isPending ? "Saving…" : group ? "Save changes" : "Create group"}</button>
        </div>
      </div>
    </div>
  );
}

/* --------------------- Who's available now (live presence strip) --------------------- */
const PRES = {
  available: { label: "Available", dot: "bg-emerald-500", order: 0 },
  busy: { label: "Busy", dot: "bg-red-500", order: 1 },
  away: { label: "Away", dot: "bg-amber-500", order: 2 },
  dnd: { label: "DND", dot: "bg-purple-500", order: 3 },
  offline: { label: "Offline", dot: "bg-muted-foreground/40", order: 4 },
};

function PresenceStrip() {
  const call = useCall();
  const navigate = useNavigate();
  const [dept, setDept] = useState("all");
  const { data } = useQuery({
    queryKey: ["pbx-extensions", ""],
    queryFn: async () => (await api.get("/pbx/extensions")).data,
    refetchInterval: 10000,
  });
  const rows = (data?.extensions || []).filter((e) => e.user_id && !e.system);
  if (rows.length === 0) return null;
  const depts = Array.from(new Set(rows.map((e) => e.department_label))).filter(Boolean);
  const shown = (dept === "all" ? rows : rows.filter((e) => e.department_label === dept))
    .slice()
    .sort((a, b) => (PRES[a.presence]?.order ?? 9) - (PRES[b.presence]?.order ?? 9));
  const onCall = (e) => {
    if (e.presence === "available" && e.online) { call?.startCall?.(e.user_id, false); toast.success(`Calling ${e.name} · ${e.ext}`); }
    else navigate(`/app/call`);
  };
  const liveCount = rows.filter((e) => e.presence === "available").length;

  return (
    <div className="rounded-2xl border border-white/10 bg-card p-4" data-testid="presence-strip">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>
          <h3 className="font-display text-sm font-semibold">Who's available now</h3>
          <span className="text-xs text-muted-foreground">{liveCount} of {rows.length} available</span>
        </div>
        {depts.length > 1 && (
          <select data-testid="presence-dept-filter" value={dept} onChange={(e) => setDept(e.target.value)}
            className="rounded-lg border border-white/10 bg-background px-2 py-1 text-xs">
            <option value="all">All departments</option>
            {depts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {shown.map((e) => {
          const p = PRES[e.presence] || PRES.offline;
          const callable = e.presence === "available" && e.online;
          return (
            <div key={e.id} data-testid={`presence-agent-${e.ext}`}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-background/60 py-1 pl-2.5 pr-1.5 text-xs">
              <span className={`h-2 w-2 rounded-full ${p.dot}`} title={p.label} />
              <span className="font-medium">{e.name}</span>
              <span className="font-mono-metric text-muted-foreground">{e.ext}</span>
              <button data-testid={`presence-call-${e.ext}`} onClick={() => onCall(e)} disabled={!callable}
                title={callable ? "Call now" : `${p.label} — open dialer`}
                className={`flex h-6 w-6 items-center justify-center rounded-full ${callable ? "bg-primary text-primary-foreground hover:scale-110" : "bg-secondary text-muted-foreground"} transition-transform`}>
                <PhoneCall size={13} weight="fill" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
